import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useSelector } from 'react-redux';
import { Box, Typography, InputBase, makeStyles } from "@material-ui/core"
import * as Yup from 'yup';
import _ from 'lodash';
import Dialog from '../../../components/Dialog';
import { patientSurveyAPI } from '../../../apis';
import { classNames } from '../../../utils';
import { checkSubmitData } from '../../../utils/check';
import { i18n } from '../../../language';
import { AutoCompleteSelect, AutoCompleteInput } from '../../../components'
import SortableListForm from '../components/SortableListForm'
import { OTH_OTHERS_ID, SERVICE_TYPE, HAHO, SF_CLINIC, CFA_TYPE_APPRECIATION } from '../../../comm/constant'


const useStyles = makeStyles((theme) => ({
  label: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#7d86a9',
    marginBottom: '0.625rem',
  },
  InputBase: {
    height: '3.5rem',
    borderRadius: '0.625rem',
    border: 'solid 1px #e4e7eb',
    width: '100%',
    paddingLeft: '1.25rem',
    '& .MuiInputBase-input': {
      fontSize: '1rem',
      color: '#2e4469',
      fontFamily: 'NotoSans-Medium',
    }
  },
  labelError: {
    color: '#f44336',
  },
  inputError: {
    border: '1px solid #f44336',
  },
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  checkBox: {
    marginLeft: '0rem',
    '& .MuiButtonBase-root': {
      padding: '0',
      paddingRight: '0.938rem',
    },
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1.125rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
}))

const EditAppreciationPopup = ({ handleClose, handleConfirm, data }) => {
  const classes = useStyles();

  const loginInfo = useSelector((state) => state.loginInfo);

  const [errorMsg, setErrorMsg] = useState(null);

  const [formData, setFormData] = useState({
    responseId: data.responseId,
    responseDetailId: data.responseDetailId,
    surveyType: data.type,
    serviceId: data.serviceId,
    departmentText: data.departmentEnDesc || data.departmentText,
    ipasWardLocation: data.ipasWardLocation,
    appreciatedStaff: data.appreciateStaff || '',
    appreciationNature: data.appreciationNature
  });

  const [questionList, setQuestionList] = useState(null)
  const [cfaTypeList, setCfaTypeList] = useState([])
  const [selectedNatures, setSelectedNatures] = useState([])

  useEffect(async () => {
    try {
      const { result } = await patientSurveyAPI.getPatientSurveyConfig()
      if (result) {
        const natureList = JSON.parse(data.appreciationNature)
        let _natureList = natureList.sort((a, b) => a.answerSortBy - b.answerSortBy).map(item => { return item.optionId - 4 })
        const otherText = natureList.find(item => item.optionId === OTH_OTHERS_ID)?.answerText ?? ''
        _natureList.push(otherText)

        const _cfaTypeList = result.surveyTypeOptions.map(item => (
          {
            text: item.optionDescEn,
            value: item.optionCode
          }
        ))

        setSelectedNatures(_natureList)
        setQuestionList(result)
        setCfaTypeList(_cfaTypeList)
      }
    } catch (error) {
      console.log('Get appreciation config fail: ', error)
      handleClose()
    }
  }, [])

  const serviceOption = useMemo(() => {
    if (questionList) {
      return questionList?.hospitalServices?.filter(item => item.pecsHospitalId === data.pecsHospitalId).map(ele => ({
        ...ele,
        text: ele.serviceEnDesc,
        value: ele.serviceId
      }))
    }
  }, [data, questionList])

  const serviceChooseObj = useMemo(() => {
    if (serviceOption) {
      return serviceOption?.find(item => Number(item?.value) === Number(formData.serviceId))
    }
  }, [serviceOption, formData])

  const departmentOption = useMemo(() => {
    if (questionList) {
      const _clusterCode = data?.clusterCode
      let _filter = null
      const serviceChooseServiceType = serviceChooseObj.serviceType
      if (serviceChooseServiceType === SERVICE_TYPE.GOPC) {
        if (_clusterCode === HAHO) {
          _filter = { serviceType: serviceChooseServiceType, type: SF_CLINIC } // Get all clinic for 'Hospital Authority'
        } else {
          _filter = { serviceType: serviceChooseServiceType, clusterCode: _clusterCode }// Filter clinic by selected hospital
        }
      } else {
        _filter = { serviceId: serviceChooseObj.serviceId }
      }
      const optionList = _.filter(questionList?.specialtyOrDepartmentOrClinics, _filter)
      return optionList.map(item => ({
        ...item,
        text: item.departmentEnDesc,
        value: item.departmentEnDesc
      }))
    }
  }, [questionList, serviceChooseObj, data])

  const wardOption = useMemo(() => {
    if (questionList) {
      return questionList?.hospitalWards?.filter(item => item.ipasHospitalCode === data.ipasHospitalCode).map(ele => ({
        ...ele,
        text: ele.pecsWardEnDesc,
        value: ele.ipasWardLocation
      }))
    }
  }, [data, questionList])

  const formLabel = {
    cfaType: "CFA Type",
    service: "Service",
    specialtiesDepartmentsClinic: "Specialties / Departments / Clinic",
    wardLocation: "IPAS Ward Location",
    appreciatedStaff: "Appreciated Staff",
    natureOfAppreciation: "Nature of Appreciation"
  }

  const schema = {
    surveyType: Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: formLabel.cfaType })),
    serviceId: Yup.number()
      .typeError(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: formLabel.service }))
      .min(0, i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: formLabel.service })),
    appreciatedStaff: Yup
      .string()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .matches(
        /^[A-Za-z\u4e00-\u9fa5\s,，]+$/,
        "Please enter a valid name (English and Chinese characters)"
      ),
    appreciationNature: Yup.string()
      .test(
        "other-value-required",
        `${formLabel.natureOfAppreciation}: Please input other information`,
        (val) => {
          if (formData.surveyType === CFA_TYPE_APPRECIATION) {
            const value = JSON.parse(val)
            const hasEmptyInput = value.find(ele => ele.optionId === OTH_OTHERS_ID && !ele.answerText)
            return !hasEmptyInput
          } else {
            return true
          }
        }
      )
      .test(
        "appreciate-type-requried",
        i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: formLabel.natureOfAppreciation }),
        (val) => {
          if (formData.surveyType === CFA_TYPE_APPRECIATION) {
            const value = JSON.parse(val)
            return value && value.length > 0
          } else {
            return true
          }
        }
      )
  }

  const handleChange = (name, type) => {
    return async (event) => {
      if (type === 'text') {
        setFormData(prev => ({
          ...prev,
          [name]: event.target.value
        }));
      } else if (type === 'select') {
        setFormData(prev => ({
          ...prev,
          [name]: event
        }));
      }
    }
  }

  const focusCheck = (name) => {
    return async () => {
      try {
        await checkSubmitData(Yup.object().shape({ [name]: schema[name] }), { [name]: formData[name] });
        setErrorMsg({ ...errorMsg, [name]: null });
      } catch (e) {
        setErrorMsg({ ...errorMsg, ...e });
      }
    };
  };

  const submitAppreciation = async () => {
    try {
      await checkSubmitData(Yup.object().shape(schema), formData);
      setErrorMsg(null);
      let requestData = formData
      const chooseDepartmentObj = departmentOption.find(item => item.value === formData.departmentText)
      requestData = {
        ...requestData,
        departmentId: chooseDepartmentObj?.departmentId || null,
        departmentText: chooseDepartmentObj ? null : formData.departmentText,
        corpId: loginInfo.corpID,
      }
      const result = await patientSurveyAPI.updateAppreciation(requestData);
      if (result) {
        handleConfirm();
        handleClose();
      }
    } catch (error) {
      setErrorMsg(error);
    }
  }

  const getCheckLableClasses = (key) => {
    return errorMsg?.[key] ? classNames(classes.label, classes.labelError) : classes.label;
  };

  const getCheckInputClasses = (key, cls) => {
    return errorMsg?.[key]
      ? classNames(classes.InputBase, classes.inputError, cls)
      : classNames(classes.InputBase, cls);
  };

  const handleReplace = useCallback((newValue) => {
    const prefix = i18n.t("AUTOCOMPLETE_OPTION_OTHER_PREFIX");
    const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`^${escapedPrefix}:\\s*`);
    return newValue.replace(regex, '');
  }, []);

  const filterOptions = (options, params) => {
    const filtered = options.filter(option =>
      option.text?.toLowerCase().includes(params.inputValue.toLowerCase())
    );

    if (params.inputValue !== '') {
      const sanitizedInput = handleReplace(params.inputValue);

      if (!filtered.some(opt => handleReplace(opt.text) === sanitizedInput)) {
        return [
          ...filtered,
          {
            value: `Other: ${sanitizedInput}`,
            text: `Other: ${sanitizedInput}`
          }
        ];
      }
    }

    return filtered;
  };

  return (
    <Dialog
      open={true}
      title="Edit Appreciation"
      cancel="Cancel"
      confirm="Save"
      handleCancel={handleClose}
      handleClose={handleClose}
      handleConfirm={submitAppreciation}
    >
      <main>
        {cfaTypeList && cfaTypeList.length > 0 &&
          <Box display="flex" mb="1.875rem">
            <Box width="100%">
              <Typography className={getCheckLableClasses('surveyType')}>{formLabel.cfaType}</Typography>
              <AutoCompleteSelect
                id="select_surveyType"
                name="surveyType"
                value={formData.surveyType}
                placeholder={formLabel.cfaType}
                itemList={cfaTypeList}
                onChange={(data) => {
                  (async () => {
                    await handleChange('surveyType', 'select')(data);
                  })();
                }}
              />
            </Box>
          </Box>
        }
        {serviceOption && serviceOption.length > 0 &&
          < Box display="flex" mb="1.875rem">
            <Box width="100%">
              <Typography className={getCheckLableClasses('serviceId')}>{formLabel.service}</Typography>
              <AutoCompleteSelect
                id="select_service"
                name="service"
                value={formData.serviceId}
                placeholder={formLabel.service}
                itemList={serviceOption}
                onChange={(data) => {
                  (async () => {
                    await handleChange('serviceId', 'select')(data);
                    // reset department select
                    await handleChange('departmentText', 'select')('');
                  })();
                }}
                onBlur={() => {
                  (async () => {
                    await focusCheck('serviceId')();
                  })();
                }}
              />
            </Box>
          </Box>
        }
        <Box display="flex">
          {serviceChooseObj?.serviceType !== SERVICE_TYPE.AED &&
            < Box width="50%" mr="1.25rem" mb="1.875rem">
              <Typography className={getCheckLableClasses('departmentText')}>{formLabel.specialtiesDepartmentsClinic}</Typography>
              <AutoCompleteInput
                id="select_departmentText"
                name="departmentText"
                value={formData.departmentText}
                placeholder={formLabel.specialtiesDepartmentsClinic}
                itemList={departmentOption}
                onChange={(data) => {
                  (async () => {
                    await handleChange('departmentText', 'select')(data);
                  })();
                }}
                onBlur={() => {
                  (async () => {
                    await focusCheck('departmentText')();
                  })();
                }}
                maxLength={40}
                filterOptions={filterOptions}
                allowCustomValue={true}
              />
            </Box>
          }
          {serviceChooseObj?.serviceType === SERVICE_TYPE.INPATIENT &&
            <Box width="50%" mb="1.875rem">
              <Typography className={getCheckLableClasses('ipasWardLocation')}>{formLabel.wardLocation}</Typography>
              <AutoCompleteSelect
                id="select_ipasWardLocation"
                name="ipasWardLocation"
                value={formData.ipasWardLocation}
                placeholder={formLabel.wardLocation}
                itemList={wardOption}
                onChange={(data) => {
                  (async () => {
                    await handleChange('ipasWardLocation', 'select')(data);
                  })();
                }}
                onBlur={() => {
                  (async () => {
                    await focusCheck('ipasWardLocation')();
                  })();
                }}
              />
            </Box>
          }
        </Box>
        {formData.surveyType === CFA_TYPE_APPRECIATION &&
          <>
            <Box display="flex" mb="1.875rem">
              <Box width="100%">
                <Typography className={getCheckLableClasses('appreciatedStaff')}>{formLabel.appreciatedStaff}</Typography>
                <InputBase
                  id="input_appreciatedStaff"
                  name="appreciatedStaff"
                  value={formData.appreciatedStaff}
                  placeholder={formLabel.appreciatedStaff}
                  className={getCheckInputClasses('appreciatedStaff')}
                  onChange={(event) => {
                    (async () => {
                      await handleChange('appreciatedStaff', 'text')(event);
                    })();
                  }}
                  onBlur={() => {
                    (async () => {
                      await focusCheck('appreciatedStaff')();
                    })();
                  }}
                  inputProps={{ maxLength: 100 }}
                />
                {errorMsg?.appreciatedStaff ? <Typography className={classes.errorMsg}>{errorMsg.appreciatedStaff}</Typography> : ''}
              </Box>
            </Box>
            <Box gridGap="1.875rem">
              <Typography className={getCheckLableClasses('appreciationNature')}>{formLabel.natureOfAppreciation}</Typography>
              {
                questionList?.natureOfAppreciationOptions &&
                <SortableListForm
                  arr1={questionList.natureOfAppreciationOptions}
                  currentValue={selectedNatures}
                  name='appreciationNature'
                  setValue={(val) => {
                    const _appreciationNature = val.reduce((acc, item, key) => {
                      if (typeof item === "number") {
                        questionList.natureOfAppreciationOptions.forEach((ele) => {
                          if (ele.optionId === item + 4) {
                            acc.push({
                              ...ele,
                              answerSortBy: key + 1,
                              answerText: ele.optionId === OTH_OTHERS_ID ? val.find(e => typeof e === "string") : ""
                            })
                          }
                        })
                      }
                      return acc
                    }, []);
                    (async () => {
                      await handleChange('appreciationNature', 'select')(JSON.stringify(_appreciationNature))
                    })()
                  }}
                  languageCode='En'
                />
              }
              {errorMsg?.appreciationNature ? <Typography className={classes.errorMsg}>{errorMsg.appreciationNature}</Typography> : ''}
            </Box>
          </>
        }
      </main>
    </Dialog >
  )

};

export default EditAppreciationPopup;